import React from 'react';
import { Controller } from 'react-hook-form';
import { EStatementPreferencesLoadingModal } from './EStatementPreferences.loading';
import { EStatementPreferencesErrorModal } from './EStatementPreferences.error';
import { useEStatementPreferences } from './useEStatementPreferences';
import { ABBREVIATED_STATES_US } from '../../../CommonConstants';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Toggle } from '../../../CommonComponents/OPL/Toggle/Toggle';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';

interface Props {
  onClose(): void;
}

export const EStatementPreferences: React.FC<Props> = ({ onClose }) => {
  const {
    data: { queryInfo, formBag },
    actions: { onSubmit, mutation },
  } = useEStatementPreferences({ onClose });

  const { data: queryData, isLoading, isError, refetch } = queryInfo;

  if (isError) {
    return <EStatementPreferencesErrorModal onClose={onClose} onRetry={refetch} />;
  }

  if (isLoading) {
    return <EStatementPreferencesLoadingModal />;
  }

  if (!queryData) {
    return null;
  }

  const { isLoading: isUpdating } = mutation;

  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
  } = formBag;

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>eStatement Settings</ModalHeader>
      <Box as="form" mt="16" onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Box display="flex" justifyContent="space-between">
            <Text fontWeight={500} fontSize="h6" id="e-statement-receive-digital-statements">
              Receive statements digitally in the Document Vault
            </Text>
            <Toggle
              {...register('isEmailStatementEnabled')}
              size="small"
              aria-labelledby="e-statement-receive-digital-statements"
              defaultChecked={queryData.isEmailStatementEnabled}
            />
          </Box>

          <Text mt="16" fontWeight={500} color="neutral.500">
            Please confirm your profile information
          </Text>

          <Text mt="8" id="e-statement-email" fontWeight={600}>
            Email
          </Text>
          <Input
            {...register('email')}
            placeholder="you@samplecapital.com"
            leftIcon="far fa-envelope"
            formVariant={errors.email ? 'error' : undefined}
            helperText={errors.email?.message}
            defaultValue={queryData.email || ''}
            aria-labelledby="e-statement-email"
          />

          <Text mt="16" id="e-statement-mailing-address" fontWeight={600}>
            Mailing Address
          </Text>
          <Box role="group" aria-labelledby="e-statement-mailing-address">
            <Input
              {...register('addressLine1')}
              placeholder="Address Line 1"
              defaultValue={queryData.addressLine1 || ''}
              aria-label="Address Line 1"
              formVariant={errors.addressLine1 ? 'error' : undefined}
            />
            <Input
              {...register('addressLine2')}
              placeholder="Address Line 2 (Optional)"
              mt="8"
              defaultValue={queryData.addressLine2 || ''}
              aria-label="Address Line 2"
            />
            <Box display="flex" justifyContent="space-between">
              <Box mt="8" width={345}>
                <Input
                  {...register('city')}
                  placeholder="City"
                  defaultValue={queryData.city || ''}
                  aria-label="City"
                  formVariant={errors.city ? 'error' : undefined}
                />
              </Box>
              <Box mt="8" width={82}>
                <Controller
                  control={control}
                  name="state"
                  defaultValue={queryData.state}
                  render={({ field: { value } }) => (
                    <Select
                      options={ABBREVIATED_STATES_US}
                      value={
                        value
                          ? ABBREVIATED_STATES_US.find(({ value: _value }) => value === _value)
                          : null
                      }
                      placeholder="State"
                      isPortal
                      onChange={opt => {
                        if (opt) {
                          formBag.setValue('state', opt.value);
                        }
                      }}
                      aria-label="State"
                      formVariant={errors.state ? 'error' : undefined}
                    />
                  )}
                />
              </Box>
              <Box width={105} mt="8">
                <Input
                  {...register('zipCode')}
                  type="number"
                  placeholder="Zip Code"
                  defaultValue={queryData.zipCode || ''}
                  aria-label="Zip Code"
                  formVariant={errors.zipCode ? 'error' : undefined}
                />
              </Box>
            </Box>
            {(errors.addressLine1 || errors.city || errors.state || errors.zipCode) && (
              <HelperText variant="error" mt="4">
                Please enter a valid address.
              </HelperText>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>
          <Button type="submit" isLoading={isUpdating}>
            Save
          </Button>
        </ModalFooter>
      </Box>
    </Modal>
  );
};
